import { ReportData } from '../types/reportTypes';
import { codySalesOctoberData } from './codySalesOctoberData';
import { codySalesNovemberData } from './codySalesNovemberData';
import { codySalesDecemberData } from './codySalesDecemberData';

// Combine all job data from Q4 months
const allJobData = [
  ...codySalesOctoberData.jobData,
  ...codySalesNovemberData.jobData,
  ...codySalesDecemberData.jobData
];

// Calculate combined sales by source
const salesBySource = (() => {
  const sourceMap = new Map<string, any>();

  // Combine data from all three months
  const allSalesBySource = [
    ...codySalesOctoberData.salesBySource,
    ...codySalesNovemberData.salesBySource,
    ...codySalesDecemberData.salesBySource
  ];

  allSalesBySource.forEach(source => {
    const key = source.source;
    if (!sourceMap.has(key)) {
      sourceMap.set(key, {
        source: key,
        count: 0,
        totalSales: 0,
        grossProfit: 0
      });
    }

    const sourceData = sourceMap.get(key);
    sourceData.count += source.count;
    sourceData.totalSales += source.totalSales;
    sourceData.grossProfit += source.grossProfit;
  });

  return Array.from(sourceMap.values());
})();

// Calculate combined ad spend by source (all should be 0 for direct sales)
const adSpendBySource = (() => {
  const uniqueSources = new Set<string>();
  allJobData.forEach(job => uniqueSources.add(job.leadSource));

  return Array.from(uniqueSources).map(source => ({
    source: source,
    spend: 0
  }));
})();

// Calculate combined sales by trade
const salesByTrade = (() => {
  const tradeMap = new Map<string, any>();

  // Process all jobs to get trade totals
  allJobData.forEach(job => {
    const key = job.trade;

    if (!tradeMap.has(key)) {
      tradeMap.set(key, {
        trade: key,
        count: 0,
        totalSales: 0,
        grossProfit: 0,
        avgContractValue: 0,
        avgGrossMargin: 0
      });
    }

    const tradeData = tradeMap.get(key);
    tradeData.count += 1;
    tradeData.totalSales += job.contractTotal;
    tradeData.grossProfit += job.grossProfit;
  });

  // Calculate averages
  tradeMap.forEach(trade => {
    trade.avgContractValue = trade.count > 0 ? trade.totalSales / trade.count : 0;
    trade.avgGrossMargin = trade.totalSales > 0 ? trade.grossProfit / trade.totalSales : 0;
  });

  return Array.from(tradeMap.values());
})();

// Calculate Q4 totals by combining all three months
const q4Totals = {
  adSpend: codySalesOctoberData.kpis.adSpend + codySalesNovemberData.kpis.adSpend + codySalesDecemberData.kpis.adSpend,
  totalSales: codySalesOctoberData.kpis.totalSales + codySalesNovemberData.kpis.totalSales + codySalesDecemberData.kpis.totalSales,
  grossProfit: codySalesOctoberData.kpis.grossProfit + codySalesNovemberData.kpis.grossProfit + codySalesDecemberData.kpis.grossProfit,
  salesCommissions: codySalesOctoberData.kpis.salesCommissions + codySalesNovemberData.kpis.salesCommissions + codySalesDecemberData.kpis.salesCommissions,
  managerFees: codySalesOctoberData.kpis.managerFees + codySalesNovemberData.kpis.managerFees + codySalesDecemberData.kpis.managerFees,
  companyOH: codySalesOctoberData.kpis.companyOH + codySalesNovemberData.kpis.companyOH + codySalesDecemberData.kpis.companyOH,
  netProfit: codySalesOctoberData.kpis.netProfit + codySalesNovemberData.kpis.netProfit + codySalesDecemberData.kpis.netProfit
};

// Export the consolidated Q4 2025 report data
export const codyQ4Data: ReportData = {
  period: {
    month: 12, // Using December as the end month of Q4
    year: 2025,
    quarter: 4,
    startMonth: 10,
    endMonth: 12
  },
  kpis: {
    adSpend: q4Totals.adSpend,
    totalSales: q4Totals.totalSales,
    grossProfit: q4Totals.grossProfit,
    salesCommissions: q4Totals.salesCommissions,
    managerFees: q4Totals.managerFees,
    companyOH: q4Totals.companyOH,
    netProfit: q4Totals.netProfit,
    netProfitPercent: q4Totals.totalSales > 0 ? q4Totals.netProfit / q4Totals.totalSales : 0,
    grossProfitPercent: q4Totals.totalSales > 0 ? q4Totals.grossProfit / q4Totals.totalSales : 0
  },
  salesBySource,
  salesByTrade,
  adSpendBySource,
  jobData: allJobData
};

// Export monthly breakdown separately for the Q4 component
export const codyQ4MonthlyBreakdown = [
  {
    month: 'October',
    totalSales: codySalesOctoberData.kpis.totalSales,
    grossProfit: codySalesOctoberData.kpis.grossProfit,
    netProfit: codySalesOctoberData.kpis.netProfit,
    jobCount: codySalesOctoberData.jobData.length
  },
  {
    month: 'November',
    totalSales: codySalesNovemberData.kpis.totalSales,
    grossProfit: codySalesNovemberData.kpis.grossProfit,
    netProfit: codySalesNovemberData.kpis.netProfit,
    jobCount: codySalesNovemberData.jobData.length
  },
  {
    month: 'December',
    totalSales: codySalesDecemberData.kpis.totalSales,
    grossProfit: codySalesDecemberData.kpis.grossProfit,
    netProfit: codySalesDecemberData.kpis.netProfit,
    jobCount: codySalesDecemberData.jobData.length
  }
];
